import type { DehydratedState } from '@tanstack/react-query';
import { hydrate } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';

import { RouteAnnouncer } from '@/components/_shared/RouteAnnouncer';
import { useWidgetDialog } from '@/hooks/shared/useWidgetDialog';
import { isWidget, widgetEntryHref } from '@/utils/isWidget.web';
import { Logger } from '@/utils/logging';
import { queryClient } from '@/utils/queryClient';
import { buildDataRefreshPayload } from '@/utils/widgetDataRefresh';
import {
  getWidgetEntryRestrictions,
  getWidgetEntryScope,
} from '@/utils/widgetEntryScope';
import {
  buildWidgetEntryQueries,
  isWidgetEntryPayload,
} from '@/utils/widgetHydration';
import {
  WIDGET_MESSAGES,
  postDataRefresh,
  postPlain,
  subscribe,
} from '@/utils/widgetMessaging.web';

import type { WidgetContextValue, WidgetProviderProps } from './WidgetProvider';
import { styles } from './styles';

const INERT_WIDGET: WidgetContextValue = {
  isWidget: false,
  isShown: false,
  closeWidget: () => {},
  markLoaded: () => {},
  postDefaultDataRefresh: () => {},
  canChangeBarber: true,
  canChangeLocation: true,
};

const WidgetContext = createContext<WidgetContextValue>(INERT_WIDGET);

// Module-level so StrictMode's double effect and provider remounts post the
// boot signals once per page load.
let hasPostedBoot = false;
let hasPostedLoaded = false;

const markLoaded = (): void => {
  if (hasPostedLoaded) return;
  hasPostedLoaded = true;
  postPlain(WIDGET_MESSAGES.widgetLoaded);
};

const hydrateFromParent = (raw: unknown, source: string): void => {
  if (raw === '' || raw === null || raw === undefined) return;
  try {
    const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!isWidgetEntryPayload(parsed)) {
      Logger.debug('widget_hydration_skipped', {
        source,
        reason: 'not an entry payload',
      });
      return;
    }
    const built = buildWidgetEntryQueries(parsed);
    const queryCache = queryClient.getQueryCache();
    const queries = built.queries.filter(
      (query) => !queryCache.get(query.queryHash),
    );
    const dropped = built.dropped + built.queries.length - queries.length;
    if (queries.length === 0) {
      Logger.debug('widget_hydration_skipped', {
        source,
        reason: 'no allowed queries',
        dropped,
      });
      return;
    }
    hydrate(queryClient, { mutations: [], queries } as DehydratedState);
    if (dropped > 0) {
      Logger.debug('widget_hydration_filtered', {
        source,
        kept: queries.length,
        dropped,
      });
    }
  } catch (error) {
    Logger.debug('widget_hydration_skipped', { source, error });
  }
};

const startDataRefresh = (): (() => void) => {
  let lastSent: string | undefined;

  const publish = () => {
    const payload = buildDataRefreshPayload(
      queryClient.getQueryCache().getAll(),
    );
    if (!payload.brand && !payload.shop && !payload.barber) return;
    const serialized = JSON.stringify(payload);
    if (serialized === lastSent) return;
    lastSent = serialized;
    postDataRefresh(payload);
  };

  publish();
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'added' || event.type === 'updated') publish();
  });
};

const resetToEntryHref = (): void => {
  if (widgetEntryHref && window.location.href !== widgetEntryHref) {
    window.location.href = widgetEntryHref;
  }
};

const EmbeddedWidgetProvider = ({ children }: WidgetProviderProps) => {
  useState(() => {
    hydrateFromParent(window.name, 'window.name');
    return null;
  });
  const [restrictions] = useState(() =>
    getWidgetEntryRestrictions(getWidgetEntryScope(widgetEntryHref)),
  );
  const [shownCount, setShownCount] = useState(0);
  const isShown = shownCount > 0;
  const rootRef = useRef<View>(null);

  useEffect(() => {
    if (!hasPostedBoot) {
      hasPostedBoot = true;
      postPlain(WIDGET_MESSAGES.appLoadStart);
    }

    const cleanups = [
      subscribe(WIDGET_MESSAGES.widgetShown, () =>
        setShownCount((count) => count + 1),
      ),
      subscribe(WIDGET_MESSAGES.appInitialData, (value) =>
        hydrateFromParent(value, WIDGET_MESSAGES.appInitialData),
      ),
      startDataRefresh(),
    ];

    return () => cleanups.forEach((cleanup) => cleanup());
  }, []);

  const closeWidget = useCallback(() => {
    postPlain(WIDGET_MESSAGES.closeWidget);
    resetToEntryHref();
  }, []);

  useWidgetDialog(rootRef, { shownCount, closeWidget });

  const postDefaultDataRefresh = useCallback(() => {
    postDataRefresh({ default: true });
  }, []);

  const value = useMemo<WidgetContextValue>(
    () => ({
      isWidget: true,
      isShown,
      closeWidget,
      markLoaded,
      postDefaultDataRefresh,
      ...restrictions,
    }),
    [isShown, closeWidget, postDefaultDataRefresh, restrictions],
  );

  return (
    <WidgetContext.Provider value={value}>
      <View
        ref={rootRef}
        tabIndex={-1}
        testID="widget-dialog-root"
        style={styles.widgetRoot}
      >
        <RouteAnnouncer shownCount={shownCount} />
        {children}
      </View>
    </WidgetContext.Provider>
  );
};

export const WidgetProvider = ({ children }: WidgetProviderProps) =>
  isWidget ? (
    <EmbeddedWidgetProvider>{children}</EmbeddedWidgetProvider>
  ) : (
    <>{children}</>
  );

export const useWidget = (): WidgetContextValue => useContext(WidgetContext);
